import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import type { LoginType } from 'sl-sdk';
import { RolesEnum, rolesClassChecker } from 'sl-sdk';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { SharelockApi } from 'api';
import { resolveUniqueId, withDeviceFields } from 'auth/device';
import { setStoredJwt } from 'auth/storage';

interface Props {
  readonly onAuthenticated: () => void;
}

type LoginFormValues = {
  username: string;
  password: string;
  token?: string;
  confirmCode?: string;
};

const LoginView: React.FC<Props> = ({ onAuthenticated }) => {
  const intl = useIntl();
  const { register, handleSubmit, getValues } = useForm<LoginFormValues>({
    defaultValues: { username: '', password: '', token: '', confirmCode: '' },
  });
  const [uniqueId, setUniqueId] = useState('');
  const [otpCheck, setOtpCheck] = useState(false);
  const [deviceCheck, setDeviceCheck] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    void resolveUniqueId().then(setUniqueId);
  }, []);

  const onSubmit = handleSubmit(async values => {
    if (!uniqueId) {
      setError(
        intl.formatMessage({
          id: 'zendesk.login.error.fingerprintNotReady',
          defaultMessage: 'Device fingerprint not ready yet. Retry in a second.',
        })
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const payload = withDeviceFields(
        {
          username: values.username,
          password: values.password,
          ...(otpCheck && values.token ? { token: values.token } : {}),
          ...(deviceCheck && values.confirmCode ? { confirmCode: values.confirmCode } : {}),
        },
        uniqueId
      ) as LoginType;

      const authData = await SharelockApi.auth.login(payload);

      if ('check_device' in authData) {
        setDeviceCheck(true);
        setOtpCheck(false);
        return;
      }

      if ('check_otp' in authData) {
        setOtpCheck(true);
        setDeviceCheck(false);
        if (authData.newQrCode) {
          setQrCode(authData.newQrCode);
        }
        return;
      }

      if (!rolesClassChecker(authData.user.roles, RolesEnum.Ops)) {
        setError(
          intl.formatMessage({
            id: 'zendesk.login.error.forbidden',
            defaultMessage: 'Access forbidden for non-Ops users.',
          })
        );
        return;
      }

      setStoredJwt(authData.jwt);
      onAuthenticated();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : intl.formatMessage({ id: 'zendesk.login.error.failed', defaultMessage: 'Login failed' });
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleResendDeviceCode = async (): Promise<void> => {
    const email = getValues('username');
    if (!email || !uniqueId) {
      return;
    }
    setIsResending(true);
    setError(null);
    try {
      await SharelockApi.auth.resendDeviceConfirmationCode({ email, uniqueId });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : intl.formatMessage({
              id: 'zendesk.login.error.resendFailed',
              defaultMessage: 'Could not resend device code',
            });
      setError(message);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Box component="form" sx={{ p: 1.5 }} onSubmit={onSubmit}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle1">
          <FormattedMessage defaultMessage="Sharelock Ops login" id="zendesk.login.title" />
        </Typography>
        {qrCode ? (
          <Box>
            <Box component="img" src={qrCode} sx={{ maxWidth: '100%' }} />
            <Typography variant="caption">
              <FormattedMessage
                defaultMessage="Scan with Google Authenticator, then enter the OTP."
                id="zendesk.login.qrCodeHint"
              />
            </Typography>
          </Box>
        ) : null}
        <TextField
          autoComplete="username"
          label={intl.formatMessage({ id: 'zendesk.login.field.email', defaultMessage: 'Email' })}
          size="small"
          {...register('username', { required: true })}
        />
        <TextField
          autoComplete="current-password"
          label={intl.formatMessage({ id: 'zendesk.login.field.password', defaultMessage: 'Password' })}
          size="small"
          type="password"
          {...register('password', { required: true })}
        />
        {deviceCheck ? (
          <>
            <TextField
              autoComplete="one-time-code"
              label={intl.formatMessage({
                id: 'zendesk.login.field.deviceCode',
                defaultMessage: 'Device confirmation code',
              })}
              size="small"
              {...register('confirmCode', { required: true })}
            />
            <Button disabled={isResending} size="small" onClick={() => void handleResendDeviceCode()}>
              <FormattedMessage defaultMessage="Resend device code" id="zendesk.login.resendDeviceCode" />
            </Button>
          </>
        ) : null}
        {otpCheck ? (
          <TextField
            autoComplete="one-time-code"
            label={intl.formatMessage({ id: 'zendesk.login.field.otp', defaultMessage: 'OTP token' })}
            size="small"
            {...register('token', { required: true })}
          />
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Button disabled={isSubmitting || !uniqueId} type="submit" variant="contained">
          <FormattedMessage defaultMessage="Login" id="zendesk.login.submit" />
        </Button>
      </Stack>
    </Box>
  );
};

export default LoginView;
